import React from 'react';
import { cn } from '@/lib/utils';

export type PasswordStrength = 'weak' | 'medium' | 'strong';

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

const calculatePasswordStrength = (password: string): { strength: PasswordStrength; score: number } => {
  if (!password || password.length === 0) {
    return { strength: 'weak', score: 0 };
  }

  let score = 0;

  // Length scoring
  if (password.length >= 6) score += 1;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;

  // Character variety scoring
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = /[^a-zA-Z0-9]/.test(password);

  if (hasLowercase) score += 1;
  if (hasUppercase) score += 1;
  if (hasNumbers) score += 1;
  if (hasSpecialChars) score += 1;

  // Determine strength based on score
  if (score <= 3) return { strength: 'weak', score };
  if (score <= 5) return { strength: 'medium', score };
  return { strength: 'strong', score };
};

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({ password, className }) => {
  const { strength, score } = calculatePasswordStrength(password);

  // Don't show meter if password is empty
  if (!password || password.length === 0) {
    return null;
  }

  const strengthConfig = {
    weak: {
      color: 'bg-red-400',
      label: 'Weak',
      textColor: 'text-red-200',
      width: 'w-1/3'
    },
    medium: {
      color: 'bg-yellow-400',
      label: 'Medium',
      textColor: 'text-yellow-200',
      width: 'w-2/3'
    },
    strong: {
      color: 'bg-green-400',
      label: 'Strong',
      textColor: 'text-green-200',
      width: 'w-full'
    }
  };

  const config = strengthConfig[strength];

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/70">Password strength:</span>
        <span className={cn('font-medium', config.textColor)}>{config.label}</span>
      </div>

      {/* Strength meter bar */}
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={cn('h-full transition-all duration-300 rounded-full', config.color, config.width)}
        />
      </div>

      {/* Requirements checklist */}
      <div className="text-xs text-white/60 space-y-1">
        <div className="flex items-center gap-2">
          <div className={cn('w-1.5 h-1.5 rounded-full', password.length >= 8 ? 'bg-green-400' : 'bg-white/30')} />
          <span>At least 8 characters</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn('w-1.5 h-1.5 rounded-full', /[A-Z]/.test(password) && /[a-z]/.test(password) ? 'bg-green-400' : 'bg-white/30')} />
          <span>Mixed case letters</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn('w-1.5 h-1.5 rounded-full', /\d/.test(password) ? 'bg-green-400' : 'bg-white/30')} />
          <span>Contains numbers</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn('w-1.5 h-1.5 rounded-full', /[^a-zA-Z0-9]/.test(password) ? 'bg-green-400' : 'bg-white/30')} />
          <span>Special characters (!@#$%^&*)</span>
        </div>
      </div>
    </div>
  );
};

export { PasswordStrengthMeter };
